import { useState, useEffect, useCallback } from 'react';
import { IconClipboardXFilled } from '@tabler/icons-react';
import TarjetaPedidos from '../components/TarjetaPedidos';
import ModalCancelarPedido from '../components/ModalCancelarPedido';
import ModalDetallesPedidoSimple from '../components/ModalDetallesPedidoSimple';
import ModalCobrarPedido from '../components/ModalCobrarPedido';
import notifications from '../Utils/notifications.json';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const STATUS_LOADING_MESSAGES = {
  in_progress: 'Aceptando pedido...',
  ready: 'Cerrando pedido...',
  cancelled: 'Cancelando pedido...',
};

const STATUS_SUCCESS_NOTIF = {
  in_progress: '20',
  ready: '5',
  cancelled: '3',
};

const STATUS_ERROR_MESSAGES = {
  in_progress: 'No se pudo aceptar el pedido',
  ready: 'No se pudo cerrar el pedido',
  cancelled: 'No se pudo cancelar el pedido',
};

const sections = [
  {
    id: 'pending',
    title: 'Pendientes',
    accent: '#f87c2b',
    emptyHint: 'Los pedidos nuevos aparecerán aquí.',
  },
  {
    id: 'in_progress',
    title: 'En progreso',
    accent: '#0071e3',
    emptyHint: 'Los pedidos aceptados aparecerán aquí.',
  },
  {
    id: 'ready',
    title: 'Listos',
    accent: '#30d158',
    emptyHint: 'Los pedidos completados aparecerán aquí.',
  },
];

const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-MX');
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(iso) {
  if (!iso) return '';
  return weekdays[new Date(iso).getDay()];
}

function SectionEmpty({ accent, hint }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl mb-1"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
      >
        <IconClipboardXFilled size={22} color={accent} style={{ opacity: 0.72 }} />
      </span>
      <p
        className="m-0"
        style={{
          fontSize: '15px',
          lineHeight: '20px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.9)',
          fontFamily: FONT,
        }}
      >
        Sin pedidos
      </p>
      <p
        className="m-0"
        style={{
          fontSize: '13px',
          lineHeight: '19px',
          fontWeight: 500,
          color: 'rgba(255, 255, 255, 0.4)',
          maxWidth: '220px',
          fontFamily: FONT,
        }}
      >
        {hint}
      </p>
    </div>
  );
}

function StatusSwitch({ data, active, onChange }) {
  return (
    <div
      className="flex gap-1 p-1 rounded-2xl"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
      role="tablist"
      aria-label="Estado de pedidos"
    >
      {data.map((section) => {
        const isActive = section.id === active;
        return (
          <button
            key={section.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(section.id)}
            className={`flex items-center justify-center gap-2 flex-1 min-w-0 h-11 px-2 rounded-xl border-none cursor-pointer transition-colors duration-200 ease-out active:scale-[0.99] ${
              isActive ? 'bg-white/[0.09]' : 'bg-transparent hover:bg-white/[0.04]'
            }`}
            style={{
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: FONT,
              color: isActive ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.45)',
            }}
          >
            <span
              className="shrink-0 rounded-full transition-opacity duration-200"
              style={{ width: 6, height: 6, backgroundColor: section.accent, opacity: isActive ? 1 : 0.4 }}
            />
            <span className="truncate">{section.title}</span>
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: isActive ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.3)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {section.orders.length}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ColumnSkeleton() {
  return (
    <div
      className="flex flex-col gap-3 flex-1 min-h-0 rounded-3xl p-2.5 lg:p-3"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.022)' }}
    >
      {[0, 1].map((i) => (
        <div
          key={i}
          className="rounded-2xl animate-pulse"
          style={{ height: '190px', backgroundColor: '#141414' }}
        />
      ))}
    </div>
  );
}

function BoardSkeleton({ activeSection }) {
  return (
    <div
      className="flex gap-3 lg:gap-4 h-full pb-1 lg:overflow-x-auto pedidos-scroll"
      role="status"
      aria-label="Cargando pedidos"
    >
      {sections.map((section, index) => (
        <div
          key={section.id}
          className={`${
            section.id === activeSection ? 'flex' : 'hidden'
          } lg:flex flex-col min-h-0 min-w-0 flex-1 lg:min-w-[272px] xl:min-w-[320px] ${
            index > 0 ? 'lg:pl-5 lg:border-l lg:border-white/[0.16]' : ''
          }`}
        >
          <div className="flex items-center justify-between gap-3 px-1.5 pb-3">
            <div className="flex items-center gap-2.5">
              <span
                className="shrink-0 rounded-full"
                style={{ width: 6, height: 6, backgroundColor: section.accent, opacity: 0.25 }}
              />
              <span
                style={{
                  fontSize: '17px',
                  lineHeight: '22px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.25)',
                  fontFamily: FONT,
                }}
              >
                {section.title}
              </span>
            </div>
            <span
              className="h-7 w-12 rounded-full animate-pulse"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
            />
          </div>
          <ColumnSkeleton />
        </div>
      ))}
    </div>
  );
}

export default function GestionPedidos({ dynamicIslandRef }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [detailsSaleId, setDetailsSaleId] = useState(null);
  const [closingOrderId, setClosingOrderId] = useState(null);
  const [chargeOrder, setChargeOrder] = useState(null);
  const [chargeSubmitting, setChargeSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('pending');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const statuses = ['pending', 'in_progress', 'ready'];
      const results = await Promise.all(
        statuses.map(async (status) => {
          const res = await apiFetch(`/sales?status=${status}&limit=100`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          return data.sales || [];
        })
      );
      setOrders(results.flat());
    } catch (e) {
      console.error('Error al cargar pedidos:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleOpenCancelModal = (orderId) => {
    setOrderToCancel(orderId);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setOrderToCancel(null);
    setCancelModalOpen(false);
  };

  const changeStatus = async (orderId, status, extra = {}) => {
    const startedAt = Date.now();
    if (status === 'ready') setClosingOrderId(orderId);
    dynamicIslandRef?.current?.addAlert('loading', STATUS_LOADING_MESSAGES[status]);
    let ok = false;
    let errorMsg = STATUS_ERROR_MESSAGES[status];
    try {
      const res = await apiFetch(`/sales/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, ...extra }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        errorMsg = err.detail || STATUS_ERROR_MESSAGES[status];
        throw new Error(errorMsg);
      }
      ok = true;
    } catch (e) {
      console.error('Error al cambiar el estado del pedido:', e);
    }
    await ensureMinLoading(startedAt);
    dynamicIslandRef?.current?.clearAlerts();
    if (status === 'ready') setClosingOrderId(null);
    if (ok) {
      const notif = notifications[STATUS_SUCCESS_NOTIF[status]];
      dynamicIslandRef?.current?.addAlert(notif.type, notif.message);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, status, updated_at: new Date().toISOString() } : o
        )
      );
    } else {
      dynamicIslandRef?.current?.addAlert('error', errorMsg);
    }
    return ok;
  };

  const handleConfirmCancel = async (reason) => {
    const ok = await changeStatus(orderToCancel, 'cancelled', { cancel_reason: reason });
    handleCloseCancelModal();
    return ok;
  };

  const handleAceptarPedido = async (orderId) => {
    const ok = await changeStatus(orderId, 'in_progress');
    return ok;
  };

  const handleCerrarPedido = async (orderId) => {
    const ok = await changeStatus(orderId, 'ready');
    return ok;
  };

  // Las comandas del mesero llegan con la bandera "pending" en el método de
  // pago: no están cobradas, así que su cierre pasa por la caja.
  const handleOpenCharge = async (orderId) => {
    dynamicIslandRef?.current?.addAlert('loading', 'Cargando pedido...');
    let full = null;
    try {
      const res = await apiFetch(`/sales/${orderId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      full = await res.json();
    } catch (e) {
      console.error('Error al cargar el pedido:', e);
    }
    dynamicIslandRef?.current?.clearAlerts();
    if (full) {
      setChargeOrder(full);
    } else {
      dynamicIslandRef?.current?.addAlert('error', 'No se pudo cargar el pedido');
    }
  };

  const handleConfirmCharge = async ({ paymentMethod, orderType, customerId, promo }) => {
    if (!chargeOrder || chargeSubmitting) return;
    const saleId = chargeOrder.id;
    setChargeSubmitting(true);
    const startedAt = Date.now();
    dynamicIslandRef?.current?.addAlert('loading', 'Procesando cobro...');
    let ok = false;
    try {
      const payRes = await apiFetch(`/sales/${saleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: paymentMethod,
          sale_type: orderType,
          customer_id: customerId ?? null,
        }),
      });
      if (!payRes.ok) throw new Error(`HTTP ${payRes.status}`);

      // Una sola promoción por orden: se aplica antes de cerrar la cuenta.
      let finalTotal = Number(chargeOrder.total_amount);
      if (promo) {
        const promoRes = await apiFetch(`/sales/${saleId}/apply-discount`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: promo.source, discount_id: promo.id }),
        });
        if (!promoRes.ok) throw new Error(`HTTP ${promoRes.status}`);
        const recalculated = await promoRes.json();
        finalTotal = Number(recalculated.total_amount);
      }

      const statusRes = await apiFetch(`/sales/${saleId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'ready' }),
      });
      if (!statusRes.ok) {
        const err = await statusRes.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${statusRes.status}`);
      }

      if (customerId) {
        const pointsRes = await apiFetch(`/points-config`);
        if (pointsRes.ok) {
          const config = await pointsRes.json();
          const pesosPerPoint = Number(config.pesos_per_point) || 10;
          const earned = Math.floor(finalTotal / pesosPerPoint);
          if (earned > 0) {
            await apiFetch(`/customers/${customerId}/points`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                movement_type: 'earned',
                points: earned,
                sale_id: saleId,
                notes: 'Puntos por compra en punto de venta',
              }),
            });
          }
        }
      }

      ok = true;
    } catch (e) {
      console.error('Error al cobrar el pedido:', e);
    }
    await ensureMinLoading(startedAt);
    dynamicIslandRef?.current?.clearAlerts();
    setChargeSubmitting(false);
    if (ok) {
      const notif = notifications['15'];
      dynamicIslandRef?.current?.addAlert(notif.type, notif.message);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === saleId
            ? { ...o, status: 'ready', payment_method: paymentMethod, updated_at: new Date().toISOString() }
            : o
        )
      );
      setChargeOrder(null);
    } else {
      dynamicIslandRef?.current?.addAlert('error', 'No se pudo cobrar el pedido. Si el problema persiste, reporta el error.');
    }
  };

  const sectionData = sections.map((section) => {
    const list = orders
      .filter((o) => o.status === section.id)
      .sort((a, b) => {
        if (section.id === 'pending') return new Date(a.sale_date) - new Date(b.sale_date);
        return new Date(a.updated_at) - new Date(b.updated_at);
      });
    return { ...section, orders: list };
  });

  return (
    <div
      className="flex flex-col h-screen overflow-hidden"
      style={{ backgroundColor: '#000', fontFamily: FONT }}
    >
      <header className="shrink-0 px-4 lg:px-8 pt-5 lg:pt-6 pb-3 lg:pb-4 border-b border-white/[0.16]">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
            }}
          >
            Gestión de pedidos
          </h1>
          <p
            className="m-0 mt-1"
            style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}
          >
            Administra y da seguimiento a los pedidos
          </p>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col px-4 lg:px-8 pt-5 lg:pt-6 pb-4 lg:pb-6">
        <div className="pb-4 lg:hidden">
          <StatusSwitch data={sectionData} active={activeSection} onChange={setActiveSection} />
        </div>

        <div className="flex-1 min-h-0" aria-busy={loading}>
          {loading ? (
            <BoardSkeleton activeSection={activeSection} />
          ) : (
            <div className="flex gap-3 lg:gap-4 h-full pb-1 lg:overflow-x-auto pedidos-scroll">
              {sectionData.map((section, index) => (
                <div
                  key={section.id}
                  className={`${
                    section.id === activeSection ? 'flex' : 'hidden'
                  } lg:flex flex-col min-h-0 min-w-0 flex-1 lg:min-w-[272px] xl:min-w-[320px] ${
                    index > 0 ? 'lg:pl-5 lg:border-l lg:border-white/[0.16]' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 px-1.5 pb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className="shrink-0 rounded-full"
                        style={{ width: 6, height: 6, backgroundColor: section.accent }}
                      />
                      <h2
                        className="font-semibold m-0 truncate"
                        style={{
                          fontSize: '17px',
                          lineHeight: '22px',
                          letterSpacing: '-0.01em',
                          color: 'rgba(255, 255, 255, 0.9)',
                        }}
                      >
                        {section.title}
                      </h2>
                    </div>
                    <span
                      className="inline-flex items-center justify-center shrink-0 h-7 min-w-[28px] rounded-full px-2.5"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {section.orders.length}
                    </span>
                  </div>

                  <div
                    className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto rounded-3xl p-2.5 lg:p-3 pedidos-scroll"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.022)',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    {section.orders.length === 0 ? (
                      <SectionEmpty
                        accent={section.accent}
                        hint={section.emptyHint}
                      />
                    ) : (
                      section.orders.map((order) => (
                        <TarjetaPedidos
                          key={order.id}
                          nombre={order.customer_name || 'Cliente mostrador'}
                          fecha={formatDate(order.sale_date)}
                          hora={formatTime(order.sale_date)}
                          dia={formatDay(order.sale_date)}
                          unidades={order.items_count}
                          section={section.id}
                          createdAt={new Date(order.sale_date).getTime()}
                          startTime={new Date(order.updated_at).getTime()}
                          readyAt={new Date(order.updated_at).getTime()}
                          isClosing={closingOrderId === order.id}
                          requiresPayment={order.payment_method === 'pending'}
                          onCancelar={() => handleOpenCancelModal(order.id)}
                          onAceptarPedido={() => handleAceptarPedido(order.id)}
                          onCerrarPedido={() => handleCerrarPedido(order.id)}
                          onCobrar={() => handleOpenCharge(order.id)}
                          onVerDetalles={() => setDetailsSaleId(order.id)}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <ModalCancelarPedido
        isOpen={cancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
      />
      <ModalDetallesPedidoSimple
        isOpen={detailsSaleId !== null}
        saleId={detailsSaleId}
        onClose={() => setDetailsSaleId(null)}
      />
      {chargeOrder && (
        <ModalCobrarPedido
          isOpen={!!chargeOrder}
          order={chargeOrder}
          submitting={chargeSubmitting}
          onClose={() => setChargeOrder(null)}
          onConfirm={handleConfirmCharge}
        />
      )}
    </div>
  );
}
